import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Github, Command, Circle } from "lucide-react";
import { libraryMenu, navigationItems } from "../mockData";
import { Button } from "./ui/button";

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [theme, setTheme] = useState("dark");
  const location = useLocation();

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
    document.documentElement.classList.toggle("dark");
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950">
      {/* Top Navigation */}
      <nav className="sticky top-0 z-50 border-b border-emerald-900/20 bg-slate-950/80 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-8">
              <Link to="/" className="flex items-center gap-2 group">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/50 transition-transform group-hover:scale-105">
                  <Circle className="h-5 w-5 text-white" />
                </div>
                <span className="text-xl font-bold text-white">AxisNet</span>
              </Link>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-3">
              {/* GitHub */}
              <a
                target="_blank"
                href="https://github.com/murataltinisik"
                className="text-sm font-medium text-slate-300 hover:text-emerald-400 transition-colors"
              >
                <Github className="h-5 w-5" />
              </a>

              {/* Mobile Menu Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="md:hidden text-slate-300"
              >
                {sidebarOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Sidebar + Content */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex gap-8 py-8">
          {/* Sidebar */}
          <aside
            className={`${
              sidebarOpen ? "block" : "hidden"
            } md:block fixed md:sticky top-16 left-0 z-40 h-[calc(100vh-4rem)] w-64 overflow-y-auto bg-slate-950/95 md:bg-transparent backdrop-blur-xl md:backdrop-blur-none p-4 md:p-0 border-r md:border-0 border-slate-800`}
          >
            <nav className="space-y-6">
              {navigationItems.map((section, idx) => (
                <div key={idx}>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-emerald-400">
                    {section.section}
                  </h3>

                  <ul className="space-y-1">
                    {section.items.map((item, itemIdx) => (
                      <li key={itemIdx}>
                        {/* Ana Menü Linki */}
                        <Link
                          to={item.path}
                          onClick={() => setSidebarOpen(false)}
                          className={`block rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                            isActive(item.path)
                              ? "bg-emerald-500/10 text-emerald-400"
                              : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                          }`}
                        >
                          {item.title}
                        </Link>

                        {/* ALT MENÜ (children) VARSA */}
                        {item.children && (
                          <ul className="ml-4 mt-1 space-y-1">
                            {item.children.map((child, cIdx) => (
                              <li key={cIdx}>
                                <Link
                                  to={child.path}
                                  onClick={() => setSidebarOpen(false)}
                                  className={`block rounded-md px-3 py-1 text-sm transition-colors ${
                                    isActive(child.path)
                                      ? "text-emerald-400"
                                      : "text-slate-500 hover:text-emerald-300"
                                  }`}
                                >
                                  {child.title}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <div className="max-w-4xl">{children}</div>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-20 border-t border-emerald-900/20 bg-slate-950/50 backdrop-blur-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600">
                  <Command className="h-5 w-5 text-white" />
                </div>
                <span className="text-lg font-bold text-white">AxisNet</span>
              </div>
              <p className="text-sm text-slate-400">
                Build clean .NET applications faster with modular architecture
                and code generation.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Documentation</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>
                  <Link
                    to="/docs/getting-started"
                    className="hover:text-emerald-400 transition-colors"
                  >
                    Getting Started
                  </Link>
                </li>
                <li>
                  <div className="flex flex-col">
                    <Link
                      to={libraryMenu.basePath}
                      className="hover:text-emerald-400 transition-colors font-semibold"
                    >
                      {libraryMenu.title}
                    </Link>

                    {/* Dynamic Sub Links */}
                    <ul className="ml-4 mt-2 space-y-1 text-sm text-slate-400">
                      {libraryMenu.items.map((item) => (
                        <li key={item.path}>
                          <Link
                            to={item.path}
                            className="hover:text-emerald-300 transition-colors"
                          >
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
                <li>
                  <Link
                    to="/docs/cli"
                    className="hover:text-emerald-400 transition-colors"
                  >
                    CLI Tool
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>
                  <a
                    href="#"
                    className="hover:text-emerald-400 transition-colors"
                  >
                    GitHub
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-500">
              © 2025 Murat ALTINIŞIK. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
